import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VersionWatcher from "@/components/VersionWatcher";
import ShareButton from "@/components/ShareButton";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Shop from "@/pages/Shop";
import Shops from "@/pages/Shops";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import SellerDashboard from "@/pages/SellerDashboard";
import BecomeSeller from "@/pages/BecomeSeller";
import SellerAgreement from "@/pages/SellerAgreement";
import Welcome from "@/pages/Welcome";
import OrderSuccess from "@/pages/OrderSuccess";
import Wishlist from "@/pages/Wishlist";
import SellerProfile from "@/pages/SellerProfile";
import Messages from "@/pages/Messages";
import CollectionPage from "@/pages/CollectionPage";
import AdminCollections from "@/pages/AdminCollections";
import AdminSellers from "@/pages/AdminSellers";
import AdminSubscribers from "@/pages/AdminSubscribers";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Contact from "@/pages/Contact";
import Help from "@/pages/Help";
import SellerResources from "@/pages/SellerResources";
import ResourceArticle from "@/pages/ResourceArticle";
import AdminResources from "@/pages/AdminResources";
import AdminOrders from "@/pages/AdminOrders";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.slice(1);
    const OFFSET = 96; // matches scroll-mt-24 on anchored sections
    let cancelled = false;
    let count = 0;
    const step = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top;
        // Re-scroll while the target isn't aligned (absorbs layout shifts from lazy images).
        if (Math.abs(top - OFFSET) > 4) {
          el.scrollIntoView({ behavior: count === 0 ? "smooth" : "auto", block: "start" });
        }
      }
      count++;
      if (count < 18) setTimeout(step, 200);
    };
    step();
    return () => { cancelled = true; };
  }, [pathname, hash]);
  return null;
}

function Shell() {
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="fade-in">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shops" element={<Shops />} />
          <Route path="/shop/:id" element={<SellerProfile />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/sell" element={<BecomeSeller />} />
          <Route path="/seller-agreement" element={<SellerAgreement />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
          <Route path="/resources" element={<SellerResources />} />
          <Route path="/resources/:slug" element={<ResourceArticle />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/order/success" element={<OrderSuccess />} />
          <Route path="/collections/:slug" element={<CollectionPage />} />
          <Route path="/admin/collections" element={
            <ProtectedRoute roles={["admin"]}><AdminCollections /></ProtectedRoute>
          } />
          <Route path="/admin/sellers" element={
            <ProtectedRoute roles={["admin"]}><AdminSellers /></ProtectedRoute>
          } />
          <Route path="/admin/subscribers" element={
            <ProtectedRoute roles={["admin"]}><AdminSubscribers /></ProtectedRoute>
          } />
          <Route path="/admin/resources" element={
            <ProtectedRoute roles={["admin"]}><AdminResources /></ProtectedRoute>
          } />
          <Route path="/admin/orders" element={
            <ProtectedRoute roles={["admin"]}><AdminOrders /></ProtectedRoute>
          } />
          <Route path="/account" element={
            <ProtectedRoute><Account /></ProtectedRoute>
          } />
          <Route path="/seller" element={
            <ProtectedRoute roles={["seller", "admin"]}><SellerDashboard /></ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute><Messages /></ProtectedRoute>
          } />
          <Route path="/messages/:otherId" element={
            <ProtectedRoute><Messages /></ProtectedRoute>
          } />
        </Routes>
      </main>
      <Footer />
      <ShareButton />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <WishlistProvider>
            <CartProvider>
              <Shell />
              <VersionWatcher />
              <Toaster position="bottom-center" />
            </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
